import {useState} from 'react';
import {Alert} from 'antd';
import {useServerTime} from 'contexts/TimeContext';
import {isNullOrUndefined} from 'utils/general';
import {
  resolveMaintenanceNotice,
  type ResolvedMaintenanceNotice,
} from './maintenanceWindow';

const DISMISSED_KEY_PREFIX = 'cip:maintenance-dismissed:';
const CLOCK_REFRESH_MS = 30_000;

/** Formats an instant in the viewer's own locale + timezone. */
function formatInViewerTimezone(epochMs: number): string {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(epochMs));
}

/** The user-facing message for the notice's current phase. */
function buildMessage(resolved: ResolvedMaintenanceNotice): string {
  if (resolved.phase === 'in-progress') {
    return (
      'Scheduled maintenance is in progress — Code in Place may be temporarily ' +
      "unreachable. If the site doesn't load, please try again shortly."
    );
  }
  return (
    'Scheduled maintenance: Code in Place will be briefly unavailable around ' +
    `${formatInViewerTimezone(resolved.startMs)}. ` +
    'You may be unable to reach the site for a short time (~2hrs).'
  );
}

/**
 * A floating notice warning users about a scheduled maintenance window.
 *
 * Rendered as a `position: fixed` overlay (like the version-update toast in
 * `components/auth/ApplicationVersionMonitor.tsx`), so it consumes no layout
 * space and works on every surface — including the viewport-locked IDE —
 * without touching the app's height/scroll model.
 *
 * Renders nothing unless a window is configured (see `MAINTENANCE_NOTICE`),
 * the current host is in scope, and the server-synced time is within the
 * visibility interval. Dismissible while `upcoming`; shown unconditionally
 * once `in-progress`. Uses server time (not the client clock), and renders
 * nothing until that clock is available.
 *
 * @return The floating notice element, or `null` when nothing should show.
 */
export function MaintenanceNotice() {
  const serverNowMs = useServerTime(CLOCK_REFRESH_MS);
  // State only exists to force a re-render on close; the durable dismissal
  // lives in localStorage, which a write alone would not re-render.
  const [locallyDismissedId, setLocallyDismissedId] = useState<string | null>(
    null,
  );

  const resolved =
    isNullOrUndefined(serverNowMs) || Number.isNaN(serverNowMs)
      ? null
      : resolveMaintenanceNotice({
          now: serverNowMs,
          hostname: window.location.hostname,
        });

  if (isNullOrUndefined(resolved)) {
    return null;
  }

  const isDismissed =
    locallyDismissedId === resolved.id ||
    window.localStorage.getItem(DISMISSED_KEY_PREFIX + resolved.id) === 'true';
  if (resolved.phase === 'upcoming' && isDismissed) {
    return null;
  }

  const handleClose = () => {
    window.localStorage.setItem(DISMISSED_KEY_PREFIX + resolved.id, 'true');
    setLocallyDismissedId(resolved.id);
  };

  return (
    <div
      style={{
        position: 'fixed',
        // Bottom-right: clear of the top navbars and IDE toolbar, so it's
        // unobtrusive and users can leave it open.
        bottom: 12,
        right: 12,
        // Above app chrome (z-index scale tops out at 1090) but a floating
        // corner card, so it never overlaps critical content.
        zIndex: 1100,
        width: 'min(420px, calc(100vw - 24px))',
      }}
    >
      <Alert
        type="warning"
        showIcon
        closable={resolved.phase === 'upcoming'}
        onClose={handleClose}
        message={buildMessage(resolved)}
        style={{boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'}}
      />
    </div>
  );
}
